<script lang="ts">
//
  import { createEventDispatcher } from "svelte";
  import { getSignatureDisplay } from "../lib/signatureStore";

  import type { Chat, Member } from "../lib/types";

  export let isOpen = false;
  export let chat: Chat;
  export let members: Member[];

  const dispatch = createEventDispatcher();

  const close = (): void => {
    dispatch("close");
  };

  const formatLastActive = (timestamp?: string): string => {
    if (!timestamp) return "Unknown";

    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return "Online";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return date.toISOString().split('T')[0]; // ISO date format YYYY-MM-DD
  };

  const getMemberDisplay = (member: Member) => {
    if (member.signature_uuid) {
      return getSignatureDisplay(member.signature_uuid);
    }
    return { name: member.username, avatar_url: member.avatar_url };
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return "Unknown";
    return new Date(dateStr).toISOString().split('T')[0]; // ISO format YYYY-MM-DD
  };
</script>

{#if isOpen}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div class="popup-overlay" on:click={close}>
    <div class="popup-content" on:click|stopPropagation>
      <header>
        <h2>Chat Info</h2>
        <button class="close-btn" on:click={close}>×</button>
      </header>

      <div class="chat-details">
        <img src={chat.avatar_url} alt={chat.name} class="chat-avatar" />
        <h3>{chat.name}</h3>
        {#if chat.last_activity}
          <p class="last-activity">Last activity: {formatDate(chat.last_activity)}</p>
        {/if}
      </div>

      <div class="members-section">
        <h4>Members ({members.length})</h4>
        <div class="members-list">
          {#each members.sort((a, b) => Number(b.is_online) - Number(a.is_online)) as member}
            {@const memberDisplay = getMemberDisplay(member)}
            <div class="member">
              <img src={memberDisplay.avatar_url} alt={memberDisplay.name} />
              <div class="member-info">
                <span class="name">{memberDisplay.name}</span>
                <span class="status">{formatLastActive(member.last_active_at)}</span>
              </div>
            </div>
          {/each}
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.popup-content {
  background: white;
  border-radius: 8px;
  width: 100%;
  max-width: 400px;
  max-height: 80vh;
  overflow-y: auto;
}

header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #eee;
}

h2 {
  margin: 0;
  font-size: 1.2rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.close-btn:hover {
  background: #f8f9fa;
}

.chat-details {
  text-align: center;
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
}

.chat-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  margin-bottom: 1rem;
}

h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.3rem;
}

.last-activity {
  color: #666;
  margin: 0;
  font-size: 0.9rem;
}

.members-section {
  padding: 1rem;
}

h4 {
  margin: 0 0 1rem 0;
  font-size: 1rem;
  color: #333;
}

.members-list {
  max-height: 300px;
  overflow-y: auto;
}

.member {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f5f5f5;
}

.member:last-child {
  border-bottom: none;
}

.member img {
  width: 36px;
  height: 36px;
  border-radius: 50%;
}

.member-info {
  flex: 1;
}

.name {
  display: block;
  font-weight: 500;
  margin-bottom: 0.25rem;
  font-size: 0.9rem;
}

.status {
  font-size: 0.8rem;
  color: #666;
}
</style>
